/**
 * InvitePage — normal-user landing screen (mounted at "/").
 *
 * Normal users do NOT see the media catalogue or room creation. Instead they
 * are prompted to enter a display name (to mint an anonymous session) and the
 * invitation code an admin shared with them. On submit we resolve the code via
 * POST /api/rooms/join-by-code and navigate to /room/:id where RoomPage takes
 * over.
 *
 * If the user already has a session (e.g. they navigated back here), only the
 * invitation code field is shown.
 */
import { useState, type FormEvent } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { LIMITS, AppError, ErrorCode } from '@syncreel/shared';
import { Button } from '../components/ui/Button';
import { useToast } from '../components/ui/Toast';
import { joinRoomByCode } from '../api/client';
import { useSessionStore } from '../store/session';
import { AuthShell } from '../components/AuthShell';
import styles from '../components/AuthShell.module.css';

/** Maximum length for the invitation code input. */
const INVITE_CODE_MAX = 64;

/** InvitePage: display-name capture (if needed) + invitation code entry → join room. */
export function InvitePage() {
  const navigate = useNavigate();
  const location = useLocation();
  const toast = useToast();
  const user = useSessionStore((s) => s.user);
  const token = useSessionStore((s) => s.token);
  const createSession = useSessionStore((s) => s.createSession);

  // WHY: if we were redirected from a room page (no session), returnTo holds
  // the original path so we can navigate back after the user enters a name.
  const returnTo = (location.state as { returnTo?: string } | null)?.returnTo;

  const [displayName, setDisplayName] = useState<string>('');
  const [nameError, setNameError] = useState<string | null>(null);
  const [creatingSession, setCreatingSession] = useState<boolean>(false);

  const [inviteCode, setInviteCode] = useState<string>('');
  const [joining, setJoining] = useState<boolean>(false);

  // ── Resolve an invitation code and navigate to the room ───────────────────
  async function resolveAndJoin(code: string) {
    setJoining(true);
    try {
      const res = await joinRoomByCode({ invitationCode: code });
      navigate(`/room/${res.room.id}`);
    } catch (err) {
      if (err instanceof AppError && err.code === ErrorCode.E_NOT_FOUND) {
        toast.error('Invalid invitation code. Check with your host and try again.');
      } else {
        const message = err instanceof Error ? err.message : 'Failed to join room.';
        toast.error(message);
      }
    } finally {
      setJoining(false);
    }
  }

  // ── Combined submit: create session (if needed) then resolve the code ──────
  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    const code = inviteCode.trim();
    if (!code) {
      toast.error('Enter an invitation code.');
      return;
    }

    // WHY: if there is no session yet, capture a display name and mint one
    // first — the join-by-code endpoint requires a bearer token.
    if (!token || !user) {
      const trimmedName = displayName.trim();
      if (trimmedName.length < 1) {
        setNameError('Display name is required.');
        return;
      }
      if (trimmedName.length > LIMITS.DISPLAY_NAME_MAX) {
        setNameError(`Display name must be ≤ ${LIMITS.DISPLAY_NAME_MAX} characters.`);
        return;
      }
      setCreatingSession(true);
      setNameError(null);
      try {
        await createSession(trimmedName);
      } catch (err) {
        const message = err instanceof Error ? err.message : 'Failed to start session.';
        toast.error(message);
        setNameError(message);
        setCreatingSession(false);
        return;
      }
      setCreatingSession(false);
    }

    await resolveAndJoin(code);
  }

  // ── Identity-only submit (when redirected from a room with returnTo) ──────
  async function handleIdentityOnly(e: FormEvent) {
    e.preventDefault();
    const trimmedName = displayName.trim();
    if (trimmedName.length < 1) {
      setNameError('Display name is required.');
      return;
    }
    if (trimmedName.length > LIMITS.DISPLAY_NAME_MAX) {
      setNameError(`Display name must be ≤ ${LIMITS.DISPLAY_NAME_MAX} characters.`);
      return;
    }
    setCreatingSession(true);
    setNameError(null);
    try {
      await createSession(trimmedName);
      if (returnTo && returnTo.startsWith('/room/')) {
        navigate(returnTo, { replace: true });
      }
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to start session.';
      toast.error(message);
      setNameError(message);
    } finally {
      setCreatingSession(false);
    }
  }

  const nameField = (
    <div className="field">
      <label className="field__label" htmlFor="displayName">
        Display name
      </label>
      <input
        id="displayName"
        className={`input ${nameError ? 'input--error' : ''}`}
        value={displayName}
        onChange={(e) => setDisplayName(e.target.value)}
        maxLength={LIMITS.DISPLAY_NAME_MAX}
        placeholder="Your name"
        autoComplete="off"
        autoFocus
        aria-invalid={nameError ? true : undefined}
        aria-describedby={nameError ? 'displayNameError' : undefined}
      />
      {nameError ? (
        <span id="displayNameError" className={styles.error}>
          {nameError}
        </span>
      ) : null}
    </div>
  );

  // ── Identity gate (only when no session AND a returnTo redirect) ──────────
  // WHY: when a user is sent to "/" from a room page without a session, we
  // only need their display name — they already know which room to join.
  if ((!token || !user) && returnTo && returnTo.startsWith('/room/')) {
    return (
      <AuthShell headline="Who’s watching?" subline="Pick a name so everyone knows who’s in the room.">
        <form className={styles.form} onSubmit={handleIdentityOnly}>
          {nameField}
          <Button type="submit" variant="primary" loading={creatingSession} className={styles.submit}>
            Continue
          </Button>
        </form>
      </AuthShell>
    );
  }

  // ── Invitation code entry ─────────────────────────────────────────────────
  return (
    <AuthShell
      headline="Watch together. Perfectly in sync."
      subline="Enter your invitation code to join the watch party."
      footer={
        <>
          Hosting? <a href="/admin">Open the admin console</a>
        </>
      }
    >
      <form className={styles.form} onSubmit={handleSubmit}>
        {!token || !user ? nameField : null}
        <div className="field">
          <label className="field__label" htmlFor="inviteCode">
            Invitation code
          </label>
          <input
            id="inviteCode"
            className={`input ${styles.code}`}
            value={inviteCode}
            onChange={(e) => setInviteCode(e.target.value)}
            maxLength={INVITE_CODE_MAX}
            placeholder="AB12CD34"
            autoComplete="off"
            autoFocus={!!token && !!user}
            aria-label="Invitation code"
          />
        </div>
        <Button type="submit" variant="primary" loading={joining || creatingSession} className={styles.submit}>
          Join room
        </Button>
      </form>
    </AuthShell>
  );
}
